import { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Wind, Droplets, Eye, Gauge, Sun, Thermometer, X,
  CloudRain, Snowflake, Cloud, Zap, ArrowUp, ArrowDown, AlertTriangle,
} from 'lucide-react';
import type { WeatherData, WeatherDay, WeatherHour } from '@/api/weather';

const ICON_BASE = 'https://raw.githubusercontent.com/visualcrossing/WeatherIcons/main/PNG/2nd%20Set%20-%20Color';

function iconUrl(icon: string) {
  return `${ICON_BASE}/${icon}.png`;
}

function fmtDate(dateStr: string) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('tr-TR', { weekday: 'short', month: 'short', day: 'numeric' });
}

function fmtFetchedAt(dateStr: string) {
  return new Date(dateStr).toLocaleString('tr-TR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

function windDirLabel(deg: number): string {
  const dirs = ['K', 'KD', 'D', 'GD', 'G', 'GB', 'B', 'KB'];
  return dirs[Math.round(deg / 45) % 8];
}

function moonPhaseLabel(phase: number): string {
  if (phase === 0 || phase === 1) return 'Yeni Ay';
  if (phase < 0.25) return 'Hilal';
  if (phase === 0.25) return 'İlk Dördün';
  if (phase < 0.5) return 'Şişen Ay';
  if (phase === 0.5) return 'Dolunay';
  if (phase < 0.75) return 'Azalan Ay';
  if (phase === 0.75) return 'Son Dördün';
  return 'Batan Ay';
}

function severeRiskLabel(risk: number): { label: string; cls: string } {
  if (risk >= 70) return { label: 'Yüksek', cls: 'text-rose-600 font-bold' };
  if (risk >= 40) return { label: 'Orta', cls: 'text-amber-500 font-semibold' };
  if (risk >= 10) return { label: 'Düşük', cls: 'text-yellow-500' };
  return { label: 'Yok', cls: 'text-emerald-600' };
}

// ── Primitives ────────────────────────────────────────────────────────────────

function Stat({ icon, label, value, highlight }: {
  icon: React.ReactNode; label: string; value: string; highlight?: string;
}) {
  return (
    <div className="bg-white flex flex-col items-center py-2.5 px-2 gap-0.5">
      {icon}
      <div className="text-[10px] text-gray-400 leading-tight">{label}</div>
      <div className={`text-xs font-semibold leading-tight ${highlight ?? 'text-gray-700'}`}>{value}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors whitespace-nowrap ${
        active ? 'bg-blue-600 text-white' : 'text-blue-100 hover:bg-white/15'
      }`}
    >
      {children}
    </button>
  );
}

// ── Tab: Şu An ────────────────────────────────────────────────────────────────

function CurrentTab({ weather }: { weather: WeatherData }) {
  const c = weather.currentConditions;
  const today = weather.days[0];
  const severe = severeRiskLabel(c.severerisk);

  return (
    <div className="flex flex-col overflow-y-auto h-full">
      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-500 to-blue-700 px-5 pt-4 pb-3 text-white">
        <div className="flex items-center gap-4">
          <img src={iconUrl(c.icon)} alt={c.conditions} className="h-16 w-16 object-contain drop-shadow" />
          <div>
            <div className="text-5xl font-bold leading-none">{Math.round(c.temp)}°C</div>
            <div className="text-sm opacity-90 mt-1">{c.conditions}</div>
            <div className="text-xs opacity-70 mt-0.5">Hissedilen {Math.round(c.feelslike)}°C · Çiğ {Math.round(c.dew)}°C</div>
          </div>
        </div>
        {today && (
          <div className="text-xs opacity-75 mt-2">
            <ArrowUp className="inline h-3 w-3" /> {Math.round(today.tempmax)}° &nbsp;
            <ArrowDown className="inline h-3 w-3" /> {Math.round(today.tempmin)}°C
            &nbsp;·&nbsp; {weather.address}
          </div>
        )}
        {weather.description && (
          <div className="text-xs opacity-70 mt-1 leading-relaxed">{weather.description}</div>
        )}
        <div className="text-[11px] opacity-60 mt-1">Son alınma: {fmtFetchedAt(weather.fetchedAt)}</div>
      </div>

      {/* Fırtına riski banner */}
      {c.severerisk >= 10 && (
        <div className={`flex items-center gap-2 px-4 py-2 text-xs border-b ${
          c.severerisk >= 70 ? 'bg-rose-50 border-rose-200 text-rose-700' :
          c.severerisk >= 40 ? 'bg-amber-50 border-amber-200 text-amber-700' :
                               'bg-yellow-50 border-yellow-200 text-yellow-700'
        }`}>
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            Konvektif fırtına riski: <b>{severe.label}</b> ({c.severerisk}/100)
            {c.cape != null && c.cape > 0 && <span className="ml-2">CAPE: {c.cape} J/kg</span>}
            {c.cin != null && c.cin < 0 && <span className="ml-2">CIN: {c.cin} J/kg</span>}
          </span>
        </div>
      )}

      {/* Stats grid — 3 sütun, tüm alanlar */}
      <div className="grid grid-cols-3 gap-px bg-gray-100 border-b border-gray-100">
        <Stat icon={<Droplets className="h-4 w-4 text-blue-500" />}     label="Nem"           value={`${c.humidity}%`} />
        <Stat icon={<Thermometer className="h-4 w-4 text-orange-400" />} label="Çiğ Noktası"   value={`${Math.round(c.dew)}°C`} />
        <Stat icon={<Wind className="h-4 w-4 text-teal-500" />}         label="Rüzgar"         value={`${Math.round(c.windspeed)} km/h ${windDirLabel(c.winddir)}`} />
        {c.windgust != null && (
          <Stat icon={<Wind className="h-4 w-4 text-teal-300" />}       label="Ani Rüzgar"     value={`${Math.round(c.windgust)} km/h`} />
        )}
        <Stat icon={<Gauge className="h-4 w-4 text-purple-500" />}      label="Basınç"         value={`${Math.round(c.pressure)} hPa`} />
        <Stat icon={<Cloud className="h-4 w-4 text-gray-400" />}        label="Bulut"          value={`${c.cloudcover}%`} />
        <Stat icon={<Eye className="h-4 w-4 text-gray-500" />}          label="Görüş"          value={c.visibility != null ? `${c.visibility} km` : '—'} />
        <Stat icon={<CloudRain className="h-4 w-4 text-blue-400" />}    label="Yağış İhtimali" value={`${c.precipprob}%`} />
        {c.precip > 0 && (
          <Stat icon={<CloudRain className="h-4 w-4 text-blue-600" />}  label="Yağış Miktarı"  value={`${c.precip} mm`} />
        )}
        <Stat icon={<Sun className="h-4 w-4 text-yellow-500" />}        label="UV İndeks"      value={String(c.uvindex)} />
        <Stat icon={<Zap className="h-4 w-4 text-yellow-400" />}        label="Güneş Rad."     value={c.solarradiation != null ? `${c.solarradiation} W/m²` : '—'} />
        {c.solarenergy != null && (
          <Stat icon={<Zap className="h-4 w-4 text-orange-400" />}      label="Güneş Enj."     value={`${c.solarenergy.toFixed(2)} MJ/m²`} />
        )}
        <Stat
          icon={<AlertTriangle className="h-4 w-4 text-rose-400" />}
          label="Fırtına Riski"
          value={`${c.severerisk}/100 ${severe.label}`}
          highlight={severe.cls}
        />
      </div>

      {/* Güneş & Ay */}
      <div className="px-4 py-3 grid grid-cols-2 gap-3 text-xs text-gray-600 border-b">
        <div className="flex flex-col gap-1">
          <span className="font-semibold text-gray-500 text-[11px] uppercase tracking-wide">Güneş</span>
          <span>🌅 Doğuş: {c.sunrise.slice(0, 5)}</span>
          <span>🌇 Batış: {c.sunset.slice(0, 5)}</span>
        </div>
        {today && (
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-gray-500 text-[11px] uppercase tracking-wide">Ay</span>
            <span>🌙 {moonPhaseLabel(today.moonphase)}</span>
            {today.moonrise && <span>🌕 Doğuş: {today.moonrise.slice(0, 5)}</span>}
            {today.moonset  && <span>🌑 Batış: {today.moonset.slice(0, 5)}</span>}
          </div>
        )}
      </div>

      {/* CAPE / CIN detay */}
      {(c.cape != null || c.cin != null) && (
        <div className="px-4 py-3 text-xs text-gray-600 space-y-1 border-b">
          <span className="font-semibold text-gray-500 text-[11px] uppercase tracking-wide block mb-1">Konvektif Parametreler</span>
          {c.cape != null && (
            <div>CAPE (Konvektif Enerji): <b>{c.cape} J/kg</b> <span className="text-gray-400">— {c.cape > 2500 ? 'Aşırı' : c.cape > 1000 ? 'Güçlü' : c.cape > 500 ? 'Orta' : 'Zayıf'}</span></div>
          )}
          {c.cin != null && (
            <div>CIN (Konvektif Engel): <b>{c.cin} J/kg</b> <span className="text-gray-400">— {c.cin < -200 ? 'Güçlü engel' : c.cin < -50 ? 'Orta engel' : 'Zayıf engel'}</span></div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Tab: Saatlik ──────────────────────────────────────────────────────────────

function HourlyTab({ days }: { days: WeatherDay[] }) {
  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);
  const tomorrowStr = new Date(now.getTime() + 86400000).toISOString().slice(0, 10);

  const todayDay    = days.find(d => d.datetime === todayStr);
  const tomorrowDay = days.find(d => d.datetime === tomorrowStr);

  const rows: { dayLabel: string; hour: WeatherHour }[] = [];
  if (todayDay)    todayDay.hours.forEach(h    => rows.push({ dayLabel: 'Bugün', hour: h }));
  if (tomorrowDay) tomorrowDay.hours.forEach(h => rows.push({ dayLabel: 'Yarın', hour: h }));

  const nowH = now.getHours();
  const startIdx = todayDay
    ? Math.max(0, todayDay.hours.findIndex(h => parseInt(h.datetime) >= nowH))
    : 0;
  const visible = rows.slice(startIdx, startIdx + 48);

  if (visible.length === 0) {
    return <div className="p-6 text-center text-sm text-gray-400">Saatlik veri henüz yok.<br />Önbellek yenilenince görünecek.</div>;
  }

  return (
    <div className="overflow-y-auto h-full">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-gray-50 text-gray-400 uppercase tracking-wide text-[10px] shadow-sm">
          <tr>
            <th className="px-3 py-2 text-left">Saat</th>
            <th className="px-1 py-2 text-center w-7"></th>
            <th className="px-2 py-2 text-right">Sıc.</th>
            <th className="px-2 py-2 text-right">His.</th>
            <th className="px-2 py-2 text-right">Nem</th>
            <th className="px-2 py-2 text-right">Çiğ</th>
            <th className="px-2 py-2 text-right">Rüzgar</th>
            <th className="px-2 py-2 text-right">Yağış</th>
            <th className="px-2 py-2 text-right">Bulut</th>
            <th className="px-2 py-2 text-right">Görüş</th>
            <th className="px-2 py-2 text-right">UV</th>
            <th className="px-2 py-2 text-right">Rad.</th>
            <th className="px-2 py-2 text-right">Risk</th>
          </tr>
        </thead>
        <tbody>
          {visible.map(({ dayLabel, hour }, i) => {
            const hNum = parseInt(hour.datetime);
            const isNow = dayLabel === 'Bugün' && hNum === nowH;
            const severe = severeRiskLabel(hour.severerisk);
            return (
              <tr key={i} className={`border-t ${isNow ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                <td className="px-3 py-1.5 font-medium text-gray-600 whitespace-nowrap">
                  {hour.datetime.slice(0, 5)}
                  {isNow && <span className="ml-1 text-[9px] text-blue-500 font-bold">▶</span>}
                </td>
                <td className="px-1 py-1.5 text-center">
                  <img src={iconUrl(hour.icon)} alt="" className="h-5 w-5 mx-auto" />
                </td>
                <td className="px-2 py-1.5 text-right font-semibold text-gray-700">{Math.round(hour.temp)}°</td>
                <td className="px-2 py-1.5 text-right text-gray-400">{Math.round(hour.feelslike)}°</td>
                <td className="px-2 py-1.5 text-right text-blue-500">{hour.humidity}%</td>
                <td className="px-2 py-1.5 text-right text-gray-400">{Math.round(hour.dew)}°</td>
                <td className="px-2 py-1.5 text-right text-teal-600 whitespace-nowrap">
                  {Math.round(hour.windspeed)}<span className="text-[9px] text-gray-400 ml-0.5">{windDirLabel(hour.winddir)}</span>
                  {hour.windgust != null && <span className="text-[9px] text-teal-300 ml-0.5">↑{Math.round(hour.windgust)}</span>}
                </td>
                <td className={`px-2 py-1.5 text-right whitespace-nowrap ${hour.precipprob > 50 ? 'text-blue-600 font-semibold' : 'text-gray-400'}`}>
                  {hour.precipprob}%
                  {hour.precip > 0 && <span className="text-[9px] ml-0.5">{hour.precip}mm</span>}
                </td>
                <td className="px-2 py-1.5 text-right text-gray-400">{hour.cloudcover}%</td>
                <td className="px-2 py-1.5 text-right text-gray-400">{hour.visibility != null ? `${hour.visibility}km` : '—'}</td>
                <td className="px-2 py-1.5 text-right text-yellow-500">{hour.uvindex}</td>
                <td className="px-2 py-1.5 text-right text-orange-400">{hour.solarradiation != null ? `${hour.solarradiation}` : '—'}</td>
                <td className={`px-2 py-1.5 text-right ${severe.cls}`}>{hour.severerisk}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Tab: 15 Günlük ────────────────────────────────────────────────────────────

function ForecastTab({ days }: { days: WeatherDay[] }) {
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div className="overflow-y-auto h-full">
      {days.map((day) => {
        const isOpen = expanded === day.datetime;
        const severe = severeRiskLabel(day.severerisk);
        return (
          <div key={day.datetime} className="border-b last:border-0">
            <button
              className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-50 text-left"
              onClick={() => setExpanded(isOpen ? null : day.datetime)}
            >
              <div className="w-24 text-xs text-gray-500 shrink-0">{fmtDate(day.datetime)}</div>
              <img src={iconUrl(day.icon)} alt={day.conditions} className="h-6 w-6 object-contain shrink-0" />
              <div className="flex-1 text-xs text-gray-500 truncate">{day.conditions}</div>
              {day.severerisk >= 10 && (
                <span className={`text-[10px] shrink-0 ${severe.cls}`}>⚡{day.severerisk}</span>
              )}
              {day.precipprob > 20 && (
                <span className="text-[10px] text-blue-500 shrink-0">{day.precipprob}%💧</span>
              )}
              <div className="flex items-center gap-1 shrink-0">
                <span className="text-xs text-gray-400">{Math.round(day.tempmin)}°</span>
                <span className="text-xs font-semibold text-gray-700">{Math.round(day.tempmax)}°</span>
              </div>
              <span className="text-gray-300 text-xs ml-1">{isOpen ? '▲' : '▼'}</span>
            </button>

            {isOpen && (
              <div className="px-4 pb-3 bg-gray-50 text-xs text-gray-600 space-y-2">
                {day.description && (
                  <p className="text-gray-500 italic leading-relaxed">{day.description}</p>
                )}
                <div className="grid grid-cols-3 gap-x-4 gap-y-1">
                  <span>🌡 Maks: <b>{Math.round(day.tempmax)}°C</b></span>
                  <span>🌡 Min: <b>{Math.round(day.tempmin)}°C</b></span>
                  <span>🌡 Ort: <b>{Math.round(day.temp)}°C</b></span>
                  <span>🤔 His. Maks: {Math.round(day.feelslikemax)}°C</span>
                  <span>🤔 His. Min: {Math.round(day.feelslikemin)}°C</span>
                  <span>💧 Nem: {day.humidity}%</span>
                  <span>🌡 Çiğ: {Math.round(day.dew)}°C</span>
                  <span>🌬 Rüzgar: {Math.round(day.windspeed)} km/h {windDirLabel(day.winddir)}</span>
                  {day.windspeedmax != null && <span>🌬 Maks: {Math.round(day.windspeedmax)} km/h</span>}
                  {day.windspeedmean != null && <span>🌬 Ort: {Math.round(day.windspeedmean)} km/h</span>}
                  {day.windgust != null && <span>💨 Ani: {Math.round(day.windgust)} km/h</span>}
                  <span>☁ Bulut: {day.cloudcover}%</span>
                  {day.visibility != null && <span>👁 Görüş: {day.visibility} km</span>}
                  <span>📊 Basınç: {Math.round(day.pressure)} hPa</span>
                  <span>🌧 Yağış İht: {day.precipprob}%</span>
                  {day.precipcover > 0 && <span>🌧 Yağış Kapsamı: %{day.precipcover}</span>}
                  {day.precip > 0 && <span>🌧 Yağış: {day.precip} mm</span>}
                  {day.preciptype && <span>🌧 Tür: {day.preciptype.join(', ')}</span>}
                  {day.snow > 0 && <span><Snowflake className="inline h-3 w-3" /> Kar: {day.snow} cm</span>}
                  {day.snowdepth > 0 && <span><Snowflake className="inline h-3 w-3" /> Derinlik: {day.snowdepth} cm</span>}
                  <span>☀ UV: {day.uvindex}</span>
                  {day.solarradiation != null && <span>⚡ Güneş Rad: {day.solarradiation} W/m²</span>}
                  {day.solarenergy != null && <span>⚡ Güneş Enj: {day.solarenergy.toFixed(1)} MJ/m²</span>}
                  <span className={severe.cls}>⛈ Fırtına Riski: {day.severerisk}/100</span>
                  {day.cape != null && day.cape > 0 && <span>CAPE: {day.cape} J/kg</span>}
                  {day.cin != null && day.cin !== 0 && <span>CIN: {day.cin} J/kg</span>}
                  <span>🌅 Gündoğumu: {day.sunrise.slice(0, 5)}</span>
                  <span>🌇 Günbatımı: {day.sunset.slice(0, 5)}</span>
                  <span>🌙 {moonPhaseLabel(day.moonphase)}</span>
                  {day.moonrise && <span>🌕 Ay Doğuşu: {day.moonrise.slice(0, 5)}</span>}
                  {day.moonset  && <span>🌑 Ay Batışı: {day.moonset.slice(0, 5)}</span>}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────

type Tab = 'current' | 'hourly' | 'forecast';

interface WeatherModalProps {
  weather: WeatherData;
  plantName: string;
  onClose: () => void;
}

function WeatherModal({ weather, plantName, onClose }: WeatherModalProps) {
  const [tab, setTab] = useState<Tab>('current');
  const hasHourly = weather.days.some(d => d.hours.length > 0);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden"
        style={{ height: '82vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Tab bar */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-700 px-4 pt-3 pb-2 relative shrink-0">
          <button
            onClick={onClose}
            className="absolute top-2 right-2 z-10 rounded-full p-3 hover:bg-white/20 transition-colors"
          >
            <X className="h-6 w-6 text-white" />
          </button>
          <div className="text-xs font-medium text-blue-100 mb-2 pr-12">{plantName}</div>
          <div className="flex items-center gap-1">
            <TabBtn active={tab === 'current'}  onClick={() => setTab('current')}>Şu An</TabBtn>
            {hasHourly && (
              <TabBtn active={tab === 'hourly'} onClick={() => setTab('hourly')}>Saatlik</TabBtn>
            )}
            <TabBtn active={tab === 'forecast'} onClick={() => setTab('forecast')}>
              {weather.days.length} Günlük
            </TabBtn>
          </div>
        </div>

        {/* Tab content */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {tab === 'current'  && <CurrentTab weather={weather} />}
          {tab === 'hourly'   && <HourlyTab  days={weather.days} />}
          {tab === 'forecast' && <ForecastTab days={weather.days} />}
        </div>
      </div>
    </div>
  );
}

interface WeatherTriggerProps {
  weather: WeatherData;
  plantName: string;
  trigger: (open: () => void) => React.ReactNode;
}

function WeatherTrigger({ weather, plantName, trigger }: WeatherTriggerProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      {trigger(() => setOpen(true))}

      {open && createPortal(
        <WeatherModal
          weather={weather}
          plantName={plantName}
          onClose={() => setOpen(false)}
        />,
        document.body,
      )}
    </>
  );
}

// ── Cell (table trigger) ──────────────────────────────────────────────────────

interface WeatherCellProps {
  weather: WeatherData;
  plantName: string;
}

export function WeatherCell({ weather, plantName }: WeatherCellProps) {
  const c = weather.currentConditions;

  return (
    <WeatherTrigger
      weather={weather}
      plantName={plantName}
      trigger={(open) => (
        <button
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            open();
          }}
          className="flex items-center gap-1.5 text-sm hover:opacity-80 transition-opacity"
          title={c.conditions}
        >
          <img src={iconUrl(c.icon)} alt={c.conditions} className="h-6 w-6 object-contain" />
          <span className="font-medium text-gray-700">{Math.round(c.temp)}°C</span>
        </button>
      )}
    />
  );
}

interface WeatherCardProps {
  weather: WeatherData;
  plantName: string;
  className?: string;
  title?: string;
  fetchedAtLabel?: string;
  compact?: boolean;
}

export function WeatherCard({
  weather,
  plantName,
  className = '',
  title = 'Hava Durumu',
  fetchedAtLabel = 'Son alinma',
  compact = false,
}: WeatherCardProps) {
  const c = weather.currentConditions;

  return (
    <WeatherTrigger
      weather={weather}
      plantName={plantName}
      trigger={(open) => (
        <button
          className={`${className} cursor-pointer hover:shadow-md transition-shadow text-left`}
          onClick={open}
        >
          <div className="flex items-center gap-2 text-gray-700">
            <img src={iconUrl(c.icon)} alt="" className="h-4 w-4 object-contain" />
            <span className="text-sm font-semibold">{title}</span>
          </div>
          <div className="flex items-center gap-2">
            <img src={iconUrl(c.icon)} alt={c.conditions} className={`${compact ? 'h-6 w-6' : 'h-9 w-9'} object-contain`} />
            <div className={compact ? 'flex min-w-0 items-center gap-2' : undefined}>
              <div className="text-xl font-bold text-gray-900">{Math.round(c.temp)}°C</div>
              <div className="text-xs text-gray-500 truncate max-w-[80px]">{c.conditions}</div>
            </div>
          </div>
          <div title={`${fetchedAtLabel}: ${fmtFetchedAt(weather.fetchedAt)}`} className={`flex gap-2 ${compact ? 'text-[10px] text-gray-500' : 'text-xs text-gray-400'}`}>
            <span>💧 {c.humidity}%</span>
            <span>💨 {Math.round(c.windspeed)} km/h</span>
          </div>
          {!compact && <div className="text-[11px] text-gray-400">
            {fetchedAtLabel}: {fmtFetchedAt(weather.fetchedAt)}
          </div>}
        </button>
      )}
    />
  );
}
